export type VerkefniRollout = 'all' | 'beta' | 'disabled';
export type LabProvider = 'sameind' | 'landspitali';

export interface AuthModeOrgSettings {
  verkefniRollout?: VerkefniRollout;
  defaultLabProvider?: LabProvider;
  heklaReferralsEnabled?: boolean;
  storeAudioInBlob?: boolean;
  piiDailyReminder?: boolean;
  requirePiiTraining?: boolean;
}

export interface AuthModeOriginalUserShadow {
  name?: string;
}

export interface AuthModeUser {
  authAccountId: string;
  authOrgId: string;
  identityId: string;
  permissions: string[];
  disciplines: string[];
  labels?: string[];
  orgSettings?: AuthModeOrgSettings;
  id?: string;
  email?: string;
  name?: string;
  title?: string;
  initials?: string;
  impersonating?: boolean;
  originalUser?: AuthModeOriginalUserShadow;
  // Whether the doctor's læknanúmer is on file (the number itself stays server-side).
  hasLaeknanumer?: boolean;
  // Whether the user's org is linked to a Sameind stofnun (lab ordering is possible).
  hasSameindStofnun?: boolean;
}

interface AuthModeUserResponse extends Record<string, unknown> {
  authAccountId?: unknown;
  authOrgId?: unknown;
  identityId?: unknown;
  account_id?: unknown;
  org_id?: unknown;
  identity_id?: unknown;
  permissions?: unknown;
  disciplines?: unknown;
  labels?: unknown;
  orgSettings?: unknown;
  org_settings?: unknown;
  has_laeknanumer?: unknown;
  has_sameind_stofnun?: unknown;
}

const isAuthModeUserResponse = (value: unknown): value is AuthModeUserResponse =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isVerkefniRollout = (value: unknown): value is VerkefniRollout =>
  value === 'all' || value === 'beta' || value === 'disabled';

const isLabProvider = (value: unknown): value is LabProvider => value === 'sameind' || value === 'landspitali';

const getRequiredString = (fieldName: string, ...values: unknown[]): string => {
  const value = values.find((candidate): candidate is string => typeof candidate === 'string');
  if (value === undefined) {
    throw new Error(`malformed auth user response: ${fieldName} must be a string`);
  }
  return value;
};

const getOptionalString = (...values: unknown[]): string | undefined =>
  values.find((candidate): candidate is string => typeof candidate === 'string');

const getOptionalBoolean = (...values: unknown[]): boolean | undefined =>
  values.find((candidate): candidate is boolean => typeof candidate === 'boolean');

const getRequiredStringArray = (fieldName: string, value: unknown): string[] => {
  if (!isStringArray(value)) {
    throw new Error(`malformed auth user response: ${fieldName} must be an array of strings`);
  }
  return value;
};

const normalizeOrgSettings = (value: unknown): AuthModeOrgSettings | undefined => {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    throw new Error('malformed auth user response: orgSettings must be an object');
  }

  const settings: AuthModeOrgSettings = {};
  const storeAudioInBlob = value.storeAudioInBlob ?? value.store_audio_in_blob;
  if (typeof storeAudioInBlob === 'boolean') {
    settings.storeAudioInBlob = storeAudioInBlob;
  }

  const verkefniRollout = value.verkefniRollout ?? value.verkefni_rollout;
  if (verkefniRollout !== undefined) {
    if (!isVerkefniRollout(verkefniRollout)) {
      throw new Error('malformed auth user response: verkefniRollout must be all, beta, or disabled');
    }
    settings.verkefniRollout = verkefniRollout;
  }

  const piiDailyReminder = value.piiDailyReminder ?? value.pii_daily_reminder;
  if (typeof piiDailyReminder === 'boolean') {
    settings.piiDailyReminder = piiDailyReminder;
  }

  const requirePiiTraining = value.requirePiiTraining ?? value.require_pii_training;
  if (typeof requirePiiTraining === 'boolean') {
    settings.requirePiiTraining = requirePiiTraining;
  }

  const defaultLabProvider = value.defaultLabProvider ?? value.default_lab_provider;
  if (isLabProvider(defaultLabProvider)) {
    settings.defaultLabProvider = defaultLabProvider;
  }

  const heklaEnabled = value.heklaReferralsEnabled ?? value.hekla_referrals_enabled;
  if (typeof heklaEnabled === 'boolean') settings.heklaReferralsEnabled = heklaEnabled;
  return settings;
};

const normalizeOriginalUserShadow = (value: unknown): AuthModeOriginalUserShadow | undefined => {
  if (!isRecord(value)) return undefined;

  const name = getOptionalString(value.name);
  if (name === undefined) return undefined;

  const originalUser: AuthModeOriginalUserShadow = {};
  originalUser.name = name;
  return originalUser;
};

const applyShadowFields = (normalized: AuthModeUser, authResponse: AuthModeUserResponse): void => {
  const id = getOptionalString(authResponse.id);
  if (id !== undefined) {
    normalized.id = id;
  }

  const email = getOptionalString(authResponse.email);
  if (email !== undefined) {
    normalized.email = email;
  }

  const name = getOptionalString(authResponse.name);
  if (name !== undefined) {
    normalized.name = name;
  }

  const title = getOptionalString(authResponse.title);
  if (title !== undefined) {
    normalized.title = title;
  }

  const initials = getOptionalString(authResponse.initials);
  if (initials !== undefined) {
    normalized.initials = initials;
  }

  const impersonating = getOptionalBoolean(authResponse.impersonating);
  if (impersonating !== undefined) {
    normalized.impersonating = impersonating;
  }

  const originalUser = normalizeOriginalUserShadow(authResponse.originalUser ?? authResponse.original_user);
  if (originalUser !== undefined) {
    normalized.originalUser = originalUser;
  }
};

export const normalizeAuthModeUser = (user: unknown): AuthModeUser => {
  if (!isAuthModeUserResponse(user)) {
    throw new Error('malformed auth user response');
  }

  const normalized: AuthModeUser = {
    authAccountId: getRequiredString('authAccountId', user.authAccountId, user.account_id),
    authOrgId: getRequiredString('authOrgId', user.authOrgId, user.org_id),
    identityId: getRequiredString('identityId', user.identityId, user.identity_id),
    permissions: getRequiredStringArray('permissions', user.permissions),
    disciplines: getRequiredStringArray('disciplines', user.disciplines),
  };

  if (isStringArray(user.labels)) {
    normalized.labels = user.labels;
  }

  const orgSettings = normalizeOrgSettings(user.orgSettings ?? user.org_settings);
  if (orgSettings !== undefined) {
    normalized.orgSettings = orgSettings;
  }

  const hasLaeknanumer = getOptionalBoolean(user.has_laeknanumer);
  if (hasLaeknanumer !== undefined) {
    normalized.hasLaeknanumer = hasLaeknanumer;
  }

  const hasSameindStofnun = getOptionalBoolean(user.has_sameind_stofnun);
  if (hasSameindStofnun !== undefined) {
    normalized.hasSameindStofnun = hasSameindStofnun;
  }

  applyShadowFields(normalized, user);

  return normalized;
};

export const getAuthUser = async (): Promise<AuthModeUser> => {
  const res = await fetch('/api/auth/user', { credentials: 'include' });
  if (!res.ok) throw new Error('not authenticated');
  return normalizeAuthModeUser(await res.json());
};

export const setLaeknanumer = async (laeknanumer: string): Promise<void> => {
  const res = await fetch('/api/auth/laeknanumer', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ laeknanumer }),
  });
  if (!res.ok) throw new Error('Ekki tókst að vista læknanúmer.');
};
